import { WorkbenchRuntimeError } from '../../shared/errors';

/**
 * @name Resource Cache
 *
 * @description Make requests to retrieve data while caching the results during your application's runtime
 *
 * @example
 * import { getCachedResource, createResourceCache } from '@workbench/core';
 *
 * // Create a new resource cache, independent from the default one
 * const getMyCachedResource = createResourceCache();
 *
 * const r1 = await getCachedResource('https://url.com/contacts.json');
 * const r2 = await getMyCachedResource('https://url.com/contacts.json');
 */

type CacheServiceInterface = (resource: string) => Promise<any>;

export const createResourceCache = (): CacheServiceInterface => {
  const cached: Record<string, unknown> = {};
  const pending: Record<string, Promise<unknown>> = {};

  return async function get(resource: string): Promise<any> {
    if (cached[resource]) {
      return cached[resource];
    }

    return new Promise((resolve, reject) => {
      if (!pending[resource]) {
        // Make a request to fetch the resource
        pending[resource] = new Promise((resolve, reject) => {
          fetch(resource)
            .then((res) => {
              if (res.ok) {
                res.text().then(resolve, reject);
              } else {
                reject();
              }
            })
            .catch(reject);
        });
      }

      pending[resource]
        .then((content: any) => {
          // Cache the resource content and resolve the promise
          resolve((cached[resource] = content));
        })
        .catch(() => {
          reject(new WorkbenchRuntimeError(`Failed to load resource: '${resource}'`, 'cache-service'));
        })
        .finally(() => {
          // clear the pending request
          delete pending[resource];
        });
    });
  };
};

export const getCachedResource = createResourceCache();
